
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users, Search, Trash2, Edit, Shield, MapPin, Phone, Calendar, FileImage, Eye, User as UserIcon, CreditCard, Globe, Home, Wallet, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import UserDetailsCard from './UserDetailsCard';
import ViewModeToggle, { ViewMode } from './views/ViewModeToggle';
import UserCardView from './views/UserCardView';
import UserListView from './views/UserListView';
import UserTableView from './views/UserTableView';

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
  kyc_status: string;
  created_at: string;
  phone?: string;
  national_id?: string;
  gender?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  id_front_url?: string;
  id_back_url?: string;
  avatar_url?: string;
  language?: string;
  university_id?: string;
  budget_max?: number;
  roommates_count?: number;
  expected_move_in?: string;
  property_ownership_type?: string;
  payout_method?: string;
  updated_at?: string;
}

const UserManagement = () => {
  const { language, t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterKYC, setFilterKYC] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const queryClient = useQueryClient();

  // Fetch users with comprehensive data
  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      console.log('🔍 Fetching comprehensive user data for admin...');
      
      // Fetch all user profiles with complete information
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          full_name,
          email,
          role,
          kyc_status,
          created_at,
          updated_at,
          phone,
          national_id,
          gender,
          address_country,
          address_city,
          address_area,
          address_street,
          address_building,
          address_apt,
          id_front_url,
          id_back_url,
          avatar_url,
          language,
          university_id,
          budget_max,
          roommates_count,
          expected_move_in,
          property_ownership_type,
          payout_method
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('❌ Error fetching comprehensive user data:', error);
        throw error;
      }
      
      console.log('✅ Comprehensive user data fetched successfully:', data?.length || 0, 'users');
      console.log('📊 User data sample:', data?.slice(0, 2)); // Log first 2 users for debugging
      return data as User[];
    }
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      console.log('🗑️ Attempting to delete user:', userId);
      
      // Use the secure delete function directly
      const { data, error } = await supabase.rpc('delete_user_account', {
        target_user_id: userId
      });
      
      if (error) {
        console.error('❌ Error deleting user:', error);
        throw new Error(error.message || 'Failed to delete user account');
      }
      
      console.log('✅ User deleted successfully:', data);
      return data;
    },
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: Error) => {
      console.error('❌ Delete user mutation error:', error);
      toast.error(`Failed to delete user: ${error.message}`);
    }
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: string }) => {
      console.log('🔄 Updating user role:', { userId, newRole });
      const { data, error } = await supabase.rpc('update_user_role', {
        target_user_id: userId,
        new_role: newRole
      });
      
      if (error) {
        console.error('❌ Error updating role:', error);
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      toast.success('User role updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: any) => {
      console.error('❌ Update role mutation error:', error);
      toast.error('Failed to update user role');
    }
  });

  // Filter users based on search and filters
  const filteredUsers = users?.filter(user => {
    // Handle null/undefined values safely
    const fullName = user.full_name || '';
    const email = user.email || '';
    const role = user.role || '';
    const kycStatus = user.kyc_status || '';
    
    const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || role === filterRole;
    const matchesKYC = filterKYC === 'all' || kycStatus === filterKYC;
    
    return matchesSearch && matchesRole && matchesKYC;
  }) || [];

  console.log('👥 Total users fetched:', users?.length || 0);
  console.log('🔍 Filtered users:', filteredUsers.length);
  console.log('🏷️ Current filters:', { searchTerm, filterRole, filterKYC });

  const handleDeleteUser = (userId: string) => {
    deleteUserMutation.mutate(userId);
  };

  const handleUpdateRole = (userId: string, newRole: string) => {
    updateRoleMutation.mutate({ userId, newRole });
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setIsUserDetailsOpen(true);
  };

  const handleCloseUserDetails = () => {
    setSelectedUser(null);
    setIsUserDetailsOpen(false);
  };

  const handleUserUpdate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const renderUserView = () => {
    const commonProps = {
      users: filteredUsers,
      onViewUser: handleViewUser,
      onUpdateRole: handleUpdateRole,
      onDeleteUser: handleDeleteUser,
      isDeleting: deleteUserMutation.isPending
    };

    switch (viewMode) {
      case 'table':
        return <UserTableView {...commonProps} />;
      case 'list':
        return <UserListView {...commonProps} />;
      case 'cards':
      default:
        return <UserCardView {...commonProps} />;
    }
  };


  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading users...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-500">
            Error loading users: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('user_management', 'إدارة المستخدمين', 'User Management')}
          </CardTitle>
          <CardDescription>
            {t('manage_user_accounts', 'إدارة حسابات المستخدمين والأذونات', 'Manage user accounts and permissions')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search, Filters, and View Toggle */}
          <div className="flex flex-col gap-4 mb-6">
            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('search_users', 'البحث في المستخدمين...', 'Search users...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="landlord">Landlord</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterKYC} onValueChange={setFilterKYC}>
                <SelectTrigger className="w-full md:w-[180px]">
                  <SelectValue placeholder="Filter by KYC" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All KYC Status</SelectItem>
                  <SelectItem value="VERIFIED">Verified</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="UNDER_REVIEW">Under Review</SelectItem>
                  <SelectItem value="REJECTED">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex justify-between items-center">
              <div className="text-sm text-muted-foreground">
                {t('showing_users', 'عرض المستخدمين', 'Showing')} {filteredUsers.length} {t('of', 'من', 'of')} {users?.length || 0} {t('users', 'مستخدمين', 'users')}
              </div>
              <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
            </div>
          </div>

          {/* User Views */}
          {renderUserView()}
        </CardContent>
      </Card>

      {/* User Details Modal */}
      {selectedUser && (
        <UserDetailsCard
          user={selectedUser}
          isOpen={isUserDetailsOpen}
          onClose={handleCloseUserDetails}
          onUpdate={handleUserUpdate}
        />
      )}
    </div>
  );
};

export default UserManagement;
